// ─── hormoneData.js ───────────────────────────────────────────────────────────
// Numerical and structural data only.
// All display text lives in hormoneTranslations.js.
// ─────────────────────────────────────────────────────────────────────────────

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
export const tokens = {
  teal:          '#4A7C6F',
  tealLight:     '#E8F2EF',
  tealMid:       '#6B9E92',
  purple:        '#7B5EA7',
  purpleLight:   '#EDE6F6',
  amber:         '#C8841A',
  amberLight:    '#FDF3E3',
  green:         '#3A8F5E',
  greenLight:    '#E6F5ED',
  red:           '#C94040',
  redLight:      '#FCEAEA',
  pink:          '#C8748A',
  pinkLight:     '#FDF0F4',
  textPrimary:   '#1C1A24',
  textSecondary: '#5A5570',
  textMuted:     '#9490A8',
  bgPage:        '#F5F4F8',
  bgCard:        '#FFFFFF',
  bgSoft:        '#F8F7FB',
  border:        '#E8E5F0',
  borderLight:   '#F0EEF6',
  shadow:        '0 1px 6px rgba(0,0,0,0.06)',
  shadowMd:      '0 2px 14px rgba(0,0,0,0.08)',
  radius:        '12px',
  radiusSm:      '8px',
  radiusXs:      '6px',
};

// ─── MARKER IDs ───────────────────────────────────────────────────────────────
// Canonical order used across charts, tables, and translation keys.
export const MARKER_IDS = [
  'LH', 'FSH', 'Prolactin',
  'E2', 'P4', 'Testosterone', 'DHEAS',
  'SHBG', 'BioT', 'TE2ratio',
];

// ─── MARKER GROUPS ────────────────────────────────────────────────────────────
// text keys (id, label, sublabel) come from t('markerGroups.<id>.*')
export const MARKER_GROUPS = [
  {
    id:      'glycoprotein',
    color:   tokens.teal,
    markers: ['LH', 'FSH', 'Prolactin'],
  },
  {
    id:      'steroid',
    color:   tokens.purple,
    markers: ['E2', 'P4', 'Testosterone', 'DHEAS'],
  },
  {
    id:      'androgen',
    color:   tokens.tealMid,
    markers: ['SHBG', 'BioT', 'TE2ratio'],
  },
];

// ─── MARKER CHART CONFIG ──────────────────────────────────────────────────────
// Which markers have cycle curves vs menopause curves, and their chart colors.
export const MARKER_CHART = {
  LH:          { hasCycleCurve: true,  hasMenoCurve: true,  color: tokens.teal,   unit: 'mIE/mL' },
  FSH:         { hasCycleCurve: true,  hasMenoCurve: true,  color: tokens.tealMid, unit: 'mIE/mL' },
  Prolactin:   { hasCycleCurve: false, hasMenoCurve: false, color: tokens.amber,   unit: 'ng/mL' },
  E2:          { hasCycleCurve: true,  hasMenoCurve: true,  color: tokens.purple,  unit: 'pg/mL' },
  P4:          { hasCycleCurve: true,  hasMenoCurve: true,  color: tokens.green,   unit: 'nmol/L' },
  Testosterone:{ hasCycleCurve: false, hasMenoCurve: true,  color: tokens.amber,   unit: 'ng/dL' },
  DHEAS:       { hasCycleCurve: false, hasMenoCurve: false, color: tokens.amber,   unit: 'µg/dL' },
  SHBG:        { hasCycleCurve: false, hasMenoCurve: false, color: tokens.teal,    unit: 'nmol/L' },
  BioT:        { hasCycleCurve: false, hasMenoCurve: false, color: tokens.tealMid, unit: 'pg/mL' },
  TE2ratio:    { hasCycleCurve: false, hasMenoCurve: false, color: tokens.red,     unit: '' },
};

// ─── PHASE COLORS ─────────────────────────────────────────────────────────────
// Purely visual — labels come from t('chart.phases.*')
export const PHASE_COLORS = {
  menstrual:  { line: tokens.pink,   bg: '#FDF0F4' },
  follicular: { line: tokens.purple, bg: '#F2EDF9' },
  ovulatory:  { line: tokens.teal,   bg: '#E8F5F2' },
  luteal:     { line: tokens.tealMid, bg: '#EAF3F0' },
};

// ─── CYCLE CURVE DATA (28 days) ───────────────────────────────────────────────
// mid = population median, low/high = approximate reference band.
// Approximate biological reference curves — shape-accurate, not lab-precise.
// Source basis: published reproductive endocrinology reference ranges.

const E2_mid  = [35,33,32,35,42,54,70,95,138,190,255,320,390,430,270,155,118,112,125,148,158,152,138,118,96,72,52,38];
const E2_low  = [14,13,12,14,18,24,34,48,68,98,145,195,250,285,155,72,50,48,55,68,78,75,68,55,42,28,18,14];
const E2_high = [62,58,55,62,72,92,118,160,218,300,390,478,555,590,425,268,198,188,205,238,252,242,218,188,158,122,92,65];

const P4_mid  = [0.8,0.8,0.8,0.8,0.9,0.9,1.0,1.0,1.1,1.1,1.2,1.5,2.2,3.5,8,16,23,29,34,38,40,37,30,20,12,6,2.5,1.2];
const P4_low  = [0.3,0.3,0.3,0.3,0.3,0.3,0.3,0.3,0.3,0.3,0.4,0.5,0.8,1.2,4,9,13,17,21,25,27,24,18,12,6,2.5,0.8,0.4];
const P4_high = [2.5,2.5,2.5,2.5,2.5,2.6,2.8,2.8,3,3,3.5,4.5,6,9,20,32,44,54,60,66,70,66,56,42,28,14,7,3.5];

const LH_mid  = [3,3,3.5,4,4.5,5.5,6.5,8,10,13,19,32,58,82,32,11,5.5,4.5,4.5,4.5,4.5,4.5,4.5,4,3.5,3.5,3,3];
const LH_low  = [1,1,1,1.5,2,2.5,3,4,5,6,9,16,32,52,15,4.5,2,1.5,1.5,1.5,1.5,1.5,1.5,1.5,1.2,1.2,1,1];
const LH_high = [8,8,9,9,10,11,13,16,19,24,36,62,105,135,68,24,14,10,9.5,9.5,9.5,9.5,9.5,9,8.5,8,8,8];

const FSH_mid = [8,8.5,9,9.5,10,10.5,11,11.5,12,12.5,13.5,14.5,16.5,14,8.5,5,4.5,4.5,4.5,4.5,4.5,4.5,5,5,5,5.5,6,7];
const FSH_low = [3,3.5,3.5,4,4,4.5,4.5,5,5,5.5,6,6.5,8.5,7,3.5,1.5,1.5,1.5,1.5,1.5,1.5,1.5,1.5,2,2,2,2.5,3];
const FSH_high= [14,14.5,15,16,16.5,17.5,18,19,20,21,22,24,27,24,15.5,9.5,8.5,8,8,8,8,8,8.5,8.5,8.5,9,10,12];

export const CYCLE_DATA = Array.from({ length: 28 }, (_, i) => ({
  day:    i + 1,
  E2mid:  E2_mid[i],  E2low:  E2_low[i],  E2high:  E2_high[i],
  P4mid:  P4_mid[i],  P4low:  P4_low[i],  P4high:  P4_high[i],
  LHmid:  LH_mid[i],  LHlow:  LH_low[i],  LHhigh:  LH_high[i],
  FSHmid: FSH_mid[i], FSHlow: FSH_low[i], FSHhigh: FSH_high[i],
}));

// ─── MENOPAUSE TRANSITION CURVES ─────────────────────────────────────────────
// x: 0 = early peri, 1 = late peri, 2 = menopause, 3 = postmenopause
// Labels come from t('chart.menoStages.*')

export const MENOPAUSE_STAGES = [
  { x: 0, labelKey: 'earlyPeri' },
  { x: 1, labelKey: 'latePeri'  },
  { x: 2, labelKey: 'menopause' },
  { x: 3, labelKey: 'postmeno'  },
];

export const MENOPAUSE_CURVES = {
  E2: {
    unit: 'pg/mL',
    natural: [
      { x: 0, mid: 158, low: 75,  high: 285 },
      { x: 1, mid: 72,  low: 12,  high: 195 },
      { x: 2, mid: 22,  low: 5,   high: 58  },
      { x: 3, mid: 12,  low: 5,   high: 35  },
    ],
    hrt: [
      { x: 0, mid: 162, low: 80,  high: 295 },
      { x: 1, mid: 108, low: 62,  high: 185 },
      { x: 2, mid: 105, low: 58,  high: 180 },
      { x: 3, mid: 100, low: 55,  high: 175 },
    ],
  },
  FSH: {
    unit: 'mIE/mL',
    natural: [
      { x: 0, mid: 12,  low: 5,  high: 26  },
      { x: 1, mid: 32,  low: 14, high: 58  },
      { x: 2, mid: 58,  low: 30, high: 95  },
      { x: 3, mid: 72,  low: 40, high: 115 },
    ],
    hrt: [
      { x: 0, mid: 11,  low: 4,  high: 24  },
      { x: 1, mid: 18,  low: 8,  high: 36  },
      { x: 2, mid: 22,  low: 10, high: 42  },
      { x: 3, mid: 26,  low: 12, high: 48  },
    ],
  },
  LH: {
    unit: 'mIE/mL',
    natural: [
      { x: 0, mid: 14,  low: 6,  high: 30  },
      { x: 1, mid: 30,  low: 12, high: 58  },
      { x: 2, mid: 46,  low: 25, high: 78  },
      { x: 3, mid: 52,  low: 28, high: 82  },
    ],
    hrt: [
      { x: 0, mid: 13,  low: 5,  high: 26  },
      { x: 1, mid: 18,  low: 8,  high: 38  },
      { x: 2, mid: 21,  low: 10, high: 42  },
      { x: 3, mid: 23,  low: 12, high: 44  },
    ],
  },
  P4: {
    unit: 'nmol/L',
    natural: [
      { x: 0, mid: 7,   low: 1,   high: 28  },
      { x: 1, mid: 1.8, low: 0.4, high: 7   },
      { x: 2, mid: 0.5, low: 0.1, high: 2   },
      { x: 3, mid: 0.3, low: 0.1, high: 1.5 },
    ],
    hrt: [
      { x: 0, mid: 8,   low: 1.5, high: 30  },
      { x: 1, mid: 18,  low: 8,   high: 42  },
      { x: 2, mid: 18,  low: 8,   high: 42  },
      { x: 3, mid: 17,  low: 7,   high: 40  },
    ],
  },
};

// ─── HRT BAND CONFIG ─────────────────────────────────────────────────────────
// Visual band drawn over the menopause transition chart when onHRT = true.
export const HRT_BAND = {
  fill:        tokens.tealLight,
  stroke:      tokens.teal,
  strokeWidth: 1.5,
  opacity:     0.35,
};

// ─── CYCLE PHASE REFERENCE WINDOWS ───────────────────────────────────────────
// Used for ReferenceArea shading on the cycle chart.
// Day ranges are relative to a 28-day cycle; scale with getCyclePhase().
export const PHASE_WINDOWS = {
  menstrual:  { startDay: 1,  endDay: 5  },
  follicular: { startDay: 6,  endDay: 12 },
  ovulatory:  { startDay: 13, endDay: 15 },
  luteal:     { startDay: 16, endDay: 28 },
};

// ─── LUTEAL P4 THRESHOLD ─────────────────────────────────────────────────────
// Below this on ~day 21 suggests anovulatory or inadequate luteal phase.
export const P4_LUTEAL_LOW_THRESHOLD = 16; // nmol/L

// ─── P4 UNIT CONVERSION ──────────────────────────────────────────────────────
// P4_mid/low/high and MENOPAUSE_CURVES.P4 above are stored in nmol/L, but lab
// results for P4 are reported in ng/mL. Standard clinical conversion factor:
// nmol/L = ng/mL × 3.18. Used by HormoneCharts.jsx to convert curve data for
// display so the chart axis matches the lab result's unit.
export const P4_NMOL_PER_NGML = 3.18;

// ─── FSH THRESHOLDS (reference, not diagnostic) ───────────────────────────────
export const FSH_THRESHOLDS = {
  ovarian_reserve_concern: 10,   // IU/L, early follicular
  perimenopause_lower:     12,
  menopause:               40,
};

// ─── QUESTIONNAIRE STATE → CHART MODE ────────────────────────────────────────
// Drives which chart type and tab logic to render.
export const STATE_CHART_MODE = {
  natural_cycle:           'cycle',
  hormonal_contraception:  'cycle',
  perimenopause:           'both',    // tabbed: transition (default) + cycle (if tracker)
  menopause:               'transition',
  menopause_HRT:           'transition',
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

/**
 * Returns the cycle phase key for a given day.
 * @param {number} cycleDay  - 1-based day in cycle
 * @param {number} cycleLength - total cycle length (default 28)
 * @returns {'menstrual'|'follicular'|'ovulatory'|'luteal'|null}
 */
export const getCyclePhase = (cycleDay, cycleLength = 28) => {
  if (!cycleDay) return null;
  const ovDay = cycleLength - 14;
  if (cycleDay <= 5)           return 'menstrual';
  if (cycleDay <= ovDay - 1)   return 'follicular';
  if (cycleDay <= ovDay + 1)   return 'ovulatory';
  return 'luteal';
};

/**
 * Returns a float 0–3 representing approximate position on the menopause
 * transition x-axis. Based on FSH + age heuristic — for visualization only.
 * @param {number} age
 * @param {number} fsh  - FSH value (mIE/mL)
 * @returns {number}  0.0–3.0
 */
export const getMenopausePosition = (age, fsh) => {
  if (!fsh || !age) return 0.5;
  if (fsh >= 40 || age >= 55) return Math.min(3.0, 3.0 + (fsh - 40) / 60);
  if (fsh >= 25 || age >= 52) return 2.0 + Math.min(0.95, (fsh - 25) / 32);
  if (fsh >= 12 || age >= 46) return 1.0 + Math.min(0.95, (fsh - 12) / 22);
  return Math.max(0, Math.min(0.95, (fsh - 5) / 10));
};

/**
 * Whether the current questionnaire state should show the HRT band modifier.
 * Perimenopause + onHRT also qualifies, handled separately in the component.
 * @param {string} hormoneState
 * @returns {boolean}
 */
export const stateHasHRT = (hormoneState) => hormoneState === 'menopause_HRT';

/**
 * Whether the current questionnaire state should show the contraception banner.
 * @param {string} hormoneState
 * @returns {boolean}
 */
export const stateHasContraception = (hormoneState) =>
  hormoneState === 'hormonal_contraception';

// ─── AI PROMPT TEMPLATES ─────────────────────────────────────────────────────
// Replace {{placeholder}} values server-side before sending to the model.

export const AI_NARRATIVE_PROMPT_TEMPLATE = `
You are a women's health interpreter for speak23, a blood-testing platform.

Write a 3–4 sentence hormone summary for this user. Be warm, direct, and scientifically grounded.

USER PROFILE:
- Age: {{user.age}}
- Hormone state: {{user.hormoneState}}  // 'natural_cycle' | 'hormonal_contraception' | 'perimenopause' | 'menopause' | 'menopause_HRT'
- Cycle day: {{user.cycleDay}} of {{user.cycleLength}}  // null if not cycling
- On HRT: {{user.onHRT}}  // true/false
- HRT type: {{user.hrtType}}  // null if not on HRT
- On hormonal contraception: {{user.onContraception}}  // true/false
- Contraception type: {{user.contraceptionType}}  // null if none

HORMONE RESULTS:
{{hormoneResults}}
// Each item: { markerId, value, unit, referenceRangeLow, referenceRangeHigh, status, phaseAdjustedStatus }

REPORTED SYMPTOMS:
- Symptoms: {{questionnaire.symptoms}}  // e.g. ['mood_swings', 'low_energy', 'hot_flashes', 'low_libido', 'bloating', 'sleep_issues', 'brain_fog']
- Mood: {{questionnaire.mood}}  // 'good' | 'neutral' | 'low' | 'anxious'
- Energy: {{questionnaire.energy}}  // 'high' | 'normal' | 'low' | 'very_low'
- Libido: {{questionnaire.libido}}  // 'high' | 'normal' | 'low' | 'very_low'

RULES:
1. Never lead with a number. Lead with what it means for this person.
2. Use "your" throughout — make it personal.
3. Name the most clinically relevant finding first.
4. If results + symptoms suggest a specific pattern (e.g. low luteal progesterone with mood symptoms, estrogen fluctuation in perimenopause), name it gently — never as a diagnosis.
5. Connect at least one hormonal finding to a symptom the user reported, if applicable.
6. If the user is on hormonal contraception, acknowledge that results should be interpreted in that context.
7. End with one grounding sentence about what this means for how she might be feeling right now.
8. Tone: warm, direct, non-dismissive. Treat the user as an intelligent adult.
9. Never use hedging phrases like "it may be possible that." Say what the data suggests.
10. Max 4 sentences. No bullet points.
11. Language: {{user.language}}  // 'de' | 'en'
`;

export const AI_DOCTOR_QUESTION_PROMPT_TEMPLATE = `
You are helping a woman prepare for a doctor's appointment after reviewing her hormone blood results on speak23.

Write ONE consolidated, specific question she can bring to her doctor. The question should:
- Include her age and life stage naturally
- Reference her most notable result(s) by name and value
- Connect the result to a symptom she reported (if applicable)
- Be worded in the first person, as if she is speaking
- Sound confident and informed — not apologetic
- Not suggest a specific diagnosis, but open the door for clinical discussion
- Be 2–4 sentences maximum

USER PROFILE:
- Age: {{user.age}}
- Hormone state: {{user.hormoneState}}
- Cycle day: {{user.cycleDay}} of {{user.cycleLength}}
- Notable results: {{notableResults}}  // [{ markerId, value, unit, status, phaseAdjustedStatus }]
- Reported symptoms: {{questionnaire.symptoms}}
- Language: {{user.language}}

Output only the question text. No preamble.
`;
